<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useReveal } from '../composables/useReveal'

const { t } = useI18n()
const { elementRef: sectionRef, isVisible } = useReveal(0.1)

const terminals = [
  { key: 'admin', color: '#0d7377' },
  { key: 'client', color: '#c45c3e' },
  { key: 'pda', color: '#1c1c1e' }
]
</script>

<template>
  <section id="terminals" ref="sectionRef" class="section terminals">
    <div class="container-narrow terminals__header">
      <div :class="['eyebrow', 'reveal', { visible: isVisible }]">{{ t('terminals.eyebrow') }}</div>
      <h2 :class="['reveal', 'reveal-delay-1', { visible: isVisible }]">{{ t('terminals.title') }}</h2>
      <p :class="['reveal', 'reveal-delay-2', { visible: isVisible }]">{{ t('terminals.subtitle') }}</p>
    </div>

    <div class="container terminals__grid">
      <article
        v-for="(terminal, index) in terminals"
        :key="terminal.key"
        :class="['terminal-card', 'reveal', `reveal-delay-${Math.min(index + 1, 4)}`, { visible: isVisible }]"
      >
        <div class="terminal-card__visual" :style="{ '--accent': terminal.color }">
          <div class="terminal-card__screen">
            <div class="terminal-card__tag">{{ t(`terminals.${terminal.key}.tag`) }}</div>
            <div class="terminal-card__preview">
              <!-- Abstract UI preview lines -->
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>

        <div class="terminal-card__body">
          <h3>{{ t(`terminals.${terminal.key}.title`) }}</h3>
          <p>{{ t(`terminals.${terminal.key}.desc`) }}</p>

          <div v-if="terminal.key === 'pda'" class="terminal-card__qr">
            <div class="qr-placeholder" aria-label="PDA Android download QR code">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="100" fill="white" />
                <path d="M10 10h30v30H10V10zm5 5v20h20V15H15z" fill="#0a0a0a" />
                <path d="M18 18h14v14H18V18z" fill="#0a0a0a" />
                <path d="M60 10h30v30H60V10zm5 5v20h20V15H65z" fill="#0a0a0a" />
                <path d="M68 18h14v14H68V18z" fill="#0a0a0a" />
                <path d="M10 60h30v30H10V60zm5 5v20h20V65H15z" fill="#0a0a0a" />
                <path d="M18 68h14v14H18V68z" fill="#0a0a0a" />
                <path d="M50 15h5v5h-5v-5zm10 0h5v5h-5v-5zm10 5h5v5h-5v-5zm-15 5h10v5H55v-5zm20 0h5v10h-5V30zm-25 5h5v5h-5v-5zm10 0h10v5H70v-5zm-10 10h5v5h-5v-5zm15 0h5v10h-5V50zm-35 0h5v5h-5v-5zm10 5h5v5h-5v-5zm-15 5h10v5H45v-5zm15 0h10v5H60v-5zm15 0h5v5h-5v-5zm10 0h5v10h-5V60zm-40 10h5v5h-5v-5zm15 0h5v10h-5V70zm10 0h10v5H70v-5zm15 0h10v5H85v-5zm-60 10h5v5h-5v-5zm15 0h5v5h-5v-5zm20 0h5v5h-5v-5zm15 0h5v5h-5v-5zm15 0h5v5h-5v-5zm10 0h5v5h-5v-5z" fill="#0a0a0a" />
              </svg>
            </div>
            <span class="qr-tip">{{ t('terminals.pda.scanTip') }}</span>
          </div>

          <a
            :href="terminal.key === 'admin' ? '/admin' : terminal.key === 'client' ? '/client' : '#pda-download'"
            :class="['btn', terminal.key === 'pda' ? 'btn-accent' : 'btn-secondary']"
          >
            {{ t(`terminals.${terminal.key}.cta`) }}
          </a>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.terminals {
  background: var(--color-bg);
}

.terminals__header {
  text-align: center;
  margin-bottom: 5rem;
}

.terminals__header h2 {
  margin-bottom: 1.25rem;
  color: var(--color-dark);
}

.terminals__header p {
  max-width: 680px;
  margin-inline: auto;
}

.terminals__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.terminal-card {
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-xl);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  overflow: hidden;
  transition: transform var(--duration-fast) var(--ease-out-expo),
    box-shadow var(--duration-fast) var(--ease-out-expo);
}

.terminal-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.1);
}

.terminal-card__visual {
  padding: 2.5rem;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.05));
  border-bottom: 1px solid var(--color-border);
}

.terminal-card__screen {
  aspect-ratio: 16 / 10;
  border-radius: var(--radius-md);
  background: var(--color-dark);
  padding: 1.5rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.terminal-card__tag {
  align-self: flex-start;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: var(--accent, var(--color-accent));
  color: white;
}

.terminal-card__preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;
}

.terminal-card__preview span {
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
}

.terminal-card__preview span:nth-child(1) { width: 70%; }
.terminal-card__preview span:nth-child(2) { width: 90%; }
.terminal-card__preview span:nth-child(3) { width: 55%; }
.terminal-card__preview span:nth-child(4) { width: 80%; }

.terminal-card__body {
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.terminal-card__body h3 {
  margin-bottom: 0.75rem;
  color: var(--color-dark);
}

.terminal-card__body p {
  flex: 1;
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.7;
}

.terminal-card__qr {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.qr-placeholder {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.qr-placeholder svg {
  width: 100%;
  height: 100%;
}

.qr-tip {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

@media (max-width: 1024px) {
  .terminals__grid {
    grid-template-columns: 1fr;
    max-width: 640px;
    margin-inline: auto;
  }

  .terminal-card__visual {
    padding: 2rem;
  }
}
</style>
